import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableContainer,
    TableSortLabel,
    Stack,
    Typography,
    Link,
    Box,
    Chip,
    IconButton,
    Alert,
    Card,
    CardContent,
    InputAdornment,
    Tooltip,
    Badge,
    Skeleton,
    Divider,
    Tabs,
    Tab,
    Pagination,
    Select,
    MenuItem,
    FormControl,
} from "@mui/material";
import { 
    Search as SearchIcon, 
    Refresh as RefreshIcon,
    FilterList as FilterIcon,
    Visibility as VisibilityIcon,
    Delete as DeleteIcon,
    People as PeopleIcon,
    Campaign as CampaignIcon,
    LocalOffer as CouponIcon,
    RestoreFromTrash as RestoreIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    AccountCircle as AccountIcon,
} from "@mui/icons-material";
import Cookies from "js-cookie";
import { getDeletedUserAccountsByCompany, getAdminCompanyId } from "../../../api/api";
import { formatAdminDate } from "../../../utils/formatDate";
import AdminHeader from "../../../components/AdminHeader/AdminHeader";
import { getAdminNavLinks } from "../../../theme/adminNavLinks";
import { pageBackgroundSx } from "../../../theme/theme";
import { styled } from "@mui/material/styles";

// Styled components for better visual appeal
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
        transform: 'translateY(-2px)',
    },
}));

// const StyledButton = styled(Button)(({ theme }) => ({
//     borderRadius: 8,
//     textTransform: 'none',
//     fontWeight: 600,
//     padding: '10px 24px',
//     boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
//     transition: 'all 0.3s ease-in-out',
//     '&:hover': {
//         transform: 'translateY(-2px)',
//         boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
//     },
// }));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: 8,
        backgroundColor: '#ffffff',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
        },
    },
}));

const DeletedCoupon = () => {
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState("");
    const [deletedUsers, setDeletedUsers] = useState([]);
    const [allDeletedUsers, setAllDeletedUsers] = useState([]); // Store all deleted users for filtering
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0
    });
    const [sortAsc, setSortAsc] = useState(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [companyName, setCompanyName] = useState("");
    const [adminName, setAdminName] = useState("");
    const [stats, setStats] = useState({
        totalDeletedUsers: 0,
        totalCampaigns: 0,
        totalCoupons: 0,
        totalPoints: 0
    });

    useEffect(() => {
        const fetchCompany = async () => {
            try {
                const admin_id = Cookies.get("admin_id");
                if (admin_id) {
                    const response = await getAdminCompanyId(admin_id);
                    setCompanyName(response.data.company.name);
                    setAdminName(response.data.admin.name);
                }
            } catch (error) {
                console.error("Error fetching company:", error);
            }
        };
        
        fetchCompany();
    }, []);

    useEffect(() => {
        const fetchDeletedUsers = async () => {
            setLoading(true);
            setError(null);
            
            try {
                // Try to get company ID from cookie or location state first
                let companyId = Cookies.get("admin_company_id") || location.state?.admin_company_id;

                // console.log("Deleted Users - Cookie admin_company_id:", Cookies.get("admin_company_id"));
                // console.log("Deleted Users - Location state admin_company_id:", location.state?.admin_company_id);
                // console.log("Deleted Users - Initial company ID:", companyId);

                // Fallback: If no company ID, try to fetch it using admin_id (like List page does)
                if (!companyId) {
                    const adminId = Cookies.get("admin_id");
                  //  console.log("Deleted Users - No company ID found, trying to fetch using admin_id:", adminId);
                    
                    if (adminId) {
                        try {
                            const response = await getAdminCompanyId(adminId);
                            companyId = response.data.company.id;
                           // console.log("Deleted Users - Fetched company ID from API:", companyId);
                            
                            // Set the cookie so we don't have to fetch again
                            Cookies.set("admin_company_id", companyId, { expires: 90, path: '/', sameSite: 'lax' });
                        } catch (error) {
                            console.error("Deleted Users - Failed to fetch company ID:", error);
                            setError("Company ID not found. Please login again.");
                            setLoading(false);
                            return;
                        }
                    } else {
                        console.log("Deleted Users - No admin_id found either");
                        setError("Company ID not found. Please login again.");
                        setLoading(false);
                        return;
                    }
                }

                if (!companyId) {
                  //  console.log("Deleted Users - Still no company ID found after all attempts");
                    setError("Company ID not found. Please login again.");
                    setLoading(false);
                    return;
                }

               // console.log("Deleted Users - Fetching data for company:", companyId, "Page:", page, "Per page:", rowsPerPage);
                
                // When searching, fetch all data (use large per_page). Otherwise use pagination
                const fetchPerPage = searchTerm.trim() ? 10000 : rowsPerPage;
                const fetchPage = searchTerm.trim() ? 1 : page;
                // Fetch deleted user accounts only
                const usersRes = await getDeletedUserAccountsByCompany(companyId, fetchPage, fetchPerPage);
                
                const usersRawData = usersRes?.data || {};
                const paginationData = usersRes?.pagination || {};

                // Convert object to array for processing
                const userRows = Object.entries(usersRawData).map(([mobile, row]) => {
                    const campaigns = row.Campaigns || [];
                    const firstCreated = campaigns.length
                        ? campaigns[0].CampaignCreated || "-"
                        : "-";

                    const deletedInfo = row.DeletedInfo || {};
                    
                    return {
                        mobileNr: mobile,
                        userName: row.UserName || "",
                        email: row.Email || "",
                        userCreatedAt: row.UserCreatedAt || "-",
                        campaigns,
                        firstCreated,
                        validCount: Number(row.TotalCoupons || 0) - Number(row.UsedCoupons || 0),
                        usedCount: Number(row.UsedCoupons || 0),
                        earnedPoints: Number(row.EarnedPoints || 0),
                        usedPoints: Number(row.UsedPoints || 0),
                        maxExpiryDate: campaigns.length ? campaigns[0].MaxExpiryDate : null,
                        deletedDate: deletedInfo.deletedDate || "-",
                        deletedBy: deletedInfo.deletedBy || "User",
                        reason: deletedInfo.reason || "User requested account deletion"
                    };
                });

                // Calculate statistics (from current page only)
                const totalCampaigns = new Set(
                    userRows.flatMap(row => 
                        row.campaigns.map(c => c.CampaignName).filter(Boolean)
                    )
                ).size;
                const totalCoupons = userRows.reduce((sum, row) => sum + Number(row.validCount || 0) + Number(row.usedCount || 0), 0);
                const totalPoints = userRows.reduce((sum, row) => sum + Number(row.earnedPoints || 0) + Number(row.usedPoints || 0), 0);

                setStats({
                    totalDeletedUsers: paginationData.total || userRows.length,
                    totalCampaigns,
                    totalCoupons,
                    totalPoints
                });

                setAllDeletedUsers(userRows); // Store all fetched deleted users
                // Only set deletedUsers directly if not searching (filter useEffect will handle search case)
                if (!searchTerm.trim()) {
                    setPagination(paginationData);
                    setDeletedUsers(userRows);
                }
            } catch (err) {
                console.error("Error loading deleted users:", err);
                const errorMessage = err.response?.data?.message || err.response?.data?.error || "Failed to load deleted user accounts. Please try again.";
                const statusCode = err.response?.status;
                const debugInfo = statusCode ? ` (Status: ${statusCode})` : '';
                setError(errorMessage + debugInfo);
                setDeletedUsers([]);
            } finally {
                setLoading(false);
            }
        };
        fetchDeletedUsers();
    }, [page, rowsPerPage, location.state?.admin_company_id, searchTerm]);

    // Client-side filtering and pagination based on search term
    useEffect(() => {
        if (!searchTerm.trim()) {
            // If no search term, use backend pagination (data already paginated)
            // Don't re-paginate, just use what was fetched
            return;
        } else {
            // Filter deleted users based on search term
            const searchLower = searchTerm.toLowerCase().trim();
            const filtered = allDeletedUsers.filter(row => {
                // Search in mobile number
                const mobileMatch = row.mobileNr?.toLowerCase().includes(searchLower);
                // Search in user name
                const nameMatch = row.userName?.toLowerCase().includes(searchLower);
                // Search in email
                const emailMatch = row.email?.toLowerCase().includes(searchLower);
                // Search in campaign names
                const campaignMatch = row.campaigns?.some(campaign => 
                    campaign.CampaignName?.toLowerCase().includes(searchLower)
                );
                // Search in deletion reason
                const reasonMatch = row.reason?.toLowerCase().includes(searchLower);
                return mobileMatch || nameMatch || emailMatch || campaignMatch || reasonMatch;
            });

            // Apply pagination to filtered results
            const offset = (page - 1) * rowsPerPage;
            const paginated = filtered.slice(offset, offset + rowsPerPage);
            setDeletedUsers(paginated);
            setPagination({
                current_page: page,
                per_page: rowsPerPage,
                total: filtered.length,
                last_page: Math.ceil(filtered.length / rowsPerPage),
                from: filtered.length > 0 ? offset + 1 : 0,
                to: Math.min(offset + rowsPerPage, filtered.length)
            });
        }
    }, [searchTerm, allDeletedUsers, page, rowsPerPage]);

    // Reset to page 1 when search term changes
    useEffect(() => {
        if (searchTerm.trim() && page !== 1) {
            setPage(1);
        }
    }, [searchTerm]);

    const handleRefresh = () => {
        window.location.reload();
    };

    const handleListAll = () => {
        setSearchTerm("");
        setPage(1);
        // This will trigger a refetch via the useEffect dependency on page
    };

    const handleSortByDate = () => {
        const sorted = [...deletedUsers].sort((a, b) => {
            const da = new Date(a.deletedDate).getTime();
            const db = new Date(b.deletedDate).getTime();
            return sortAsc ? da - db : db - da;
        });
        setDeletedUsers(sorted);
        setSortAsc(!sortAsc);
    };

    // No need for client-side pagination - backend handles it
    const paginatedUsers = Array.isArray(deletedUsers) ? deletedUsers : [];

    // Loading skeleton component
    const LoadingSkeleton = () => (
        <Box sx={{ width: "100%" }}>
            {[...Array(5)].map((_, index) => (
                <Skeleton key={index} variant="rectangular" height={60} sx={{ mb: 1 }} />
            ))}
        </Box>
    );

    // Empty state component
    const EmptyState = () => (
        <Box sx={{ textAlign: "center", py: 8 }}>
            <AccountIcon sx={{ fontSize: 80, color: "grey.400", mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
                No Deleted User Accounts Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {searchTerm ? "Try adjusting your search criteria" : "No user accounts have been deleted yet"}
            </Typography>
        </Box>
    );

    return (
            <Box sx={pageBackgroundSx}>
                <Box sx={{ maxWidth: { xs: "100%", sm: "95%" }, mx: "auto", p: { xs: 1, sm: 2 } }}>
                    <AdminHeader 
                        title="Deleted Users Report"
                        icon={DeleteIcon}
                        navigationLinks={getAdminNavLinks("/delete")}
                    />

                    {/* Statistics and Search Section */}
                    <StyledCard sx={{ mb: { xs: 2, sm: 3 } }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: { xs: 'stretch', sm: 'center' }, 
                                justifyContent: 'space-between',
                                flexWrap: 'wrap', 
                                gap: { xs: 2, sm: 2 } 
                            }}>
                                {/* Statistics */}
                                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                                    <Chip 
                                        icon={<AccountIcon />} 
                                        label={`${stats.totalDeletedUsers} Deleted Accounts`} 
                                        color="error" 
                                        variant="outlined" 
                                        size="small"
                                    />
                                    <Chip 
                                        icon={<CampaignIcon />} 
                                        label={`${stats.totalCampaigns} Campaigns`} 
                                        color="primary" 
                                        variant="outlined" 
                                        size="small"
                                    />
                                    <Chip 
                                        icon={<CouponIcon />} 
                                        label={`${stats.totalCoupons} Coupons`} 
                                        color="info" 
                                        variant="outlined" 
                                        size="small"
                                    />
                                    <Chip 
                                        icon={<PeopleIcon />} 
                                        label={`${stats.totalPoints} Points`} 
                                        color="warning" 
                                        variant="outlined" 
                                        size="small"
                                    />
                                </Stack>

                                {/* Search Bar */}
                                <StyledTextField
                                    placeholder="Search mobile, name, email, campaign, reason..."
                                    variant="outlined"
                                    size="small"
                                    sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: { xs: 0, sm: 250 } }}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                {/* Action Buttons */}
                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Clear Search">
                                        <IconButton 
                                            onClick={handleListAll} 
                                            disabled={!searchTerm}
                                            size="small"
                                        >
                                            <FilterIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Refresh Data">
                                        <IconButton onClick={handleRefresh} size="small">
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        </CardContent>
                    </StyledCard>

                    {/* Error State */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} action={
                            <Button color="inherit" size="small" onClick={handleRefresh}>
                                Retry
                            </Button>
                        }>
                            {error}
                        </Alert>
                    )}

                    {/* Main Content */}
                    <StyledCard>
                        <CardContent sx={{ p: 0 }}>
                            {loading ? (
                                <Box sx={{ p: 2 }}>
                                    <LoadingSkeleton />
                                </Box>
                            ) : deletedUsers.length === 0 ? (
                                <EmptyState />
                            ) : (
                                <>
                                    {/* Table Header */}
                                    <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                Deleted User Account Records
                                            </Typography>
                                            <Badge badgeContent={pagination.total || deletedUsers.length} color="warning">
                                                <VisibilityIcon color="action" />
                                            </Badge>
                        </Stack>
                                    </Box>

                                    {/* Table */}
                                    <TableContainer sx={{ 
                                        maxHeight: { xs: 400, sm: 600 },
                                        overflow: 'auto',
                                        overflowX: 'auto',
                                        maxWidth: '100%',
                                        '&::-webkit-scrollbar': {
                                            width: '8px',
                                            height: '8px',
                                        },
                                        '&::-webkit-scrollbar-track': {
                                            background: '#f1f1f1',
                                            borderRadius: '4px',
                                        },
                                        '&::-webkit-scrollbar-thumb': {
                                            background: '#c1c1c1',
                                            borderRadius: '4px',
                                            '&:hover': {
                                                background: '#a8a8a8',
                                            },
                                        },
                                    }}>
                                        <Table size="small" sx={{ tableLayout: { xs: 'auto', md: 'fixed' }, minWidth: 900 }}>
                                            <TableHead>
                                                            <TableRow sx={{ bgcolor: "grey.100" }}>
                                                                <TableCell sx={{ fontWeight: "bold", width: 120, padding: "8px 16px" }}>Mobile No</TableCell>
                                                                <TableCell sx={{ fontWeight: "bold", width: 120, padding: "8px 16px" }}>User Name</TableCell>
                                                                <TableCell sx={{ fontWeight: "bold", width: 150, padding: "8px 16px" }}>Campaign</TableCell>
                                                                <TableCell sx={{ fontWeight: "bold", width: 120, padding: "8px 16px" }}>Created Date</TableCell>
                                                                <TableCell sx={{ fontWeight: "bold", width: 120, padding: "8px 16px" }}>
                                                        <TableSortLabel
                                                            active
                                                            direction={sortAsc ? "asc" : "desc"}
                                                            onClick={handleSortByDate}
                                                        >
                                                                        Deleted Date
                                                        </TableSortLabel>
                                                    </TableCell>
                                                                <TableCell sx={{ fontWeight: "bold", width: 100, padding: "8px 16px" }}>Valid</TableCell>
                                                                <TableCell sx={{ fontWeight: "bold", width: 100, padding: "8px 16px" }}>Used</TableCell>
                                                                <TableCell sx={{ fontWeight: "bold", width: 100, padding: "8px 16px" }}>Earned Points</TableCell>
                                                                <TableCell sx={{ fontWeight: "bold", width: 100, padding: "8px 16px" }}>Used Points</TableCell>
                                                                <TableCell sx={{ fontWeight: "bold", width: 150, padding: "8px 16px" }}>Deletion Reason</TableCell>
                                                                <TableCell sx={{ fontWeight: "bold", width: 120, padding: "8px 16px" }}>Expiry Date</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {paginatedUsers.map((row, index) => (
                                                    <TableRow 
                                                        key={index}
                                                        hover
                                                        sx={{ 
                                                            "&:hover": { bgcolor: "action.hover" }
                                                        }}
                                                    >
                                                            <TableCell sx={{ width: 120, padding: "8px 16px" }}>
                                                                <Typography variant="body2" fontWeight="medium" color="primary">
                                                                    {row.mobileNr}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 120, padding: "8px 16px" }}>
                                                                <Typography variant="body2">
                                                                    {row.userName}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 150, padding: "8px 16px" }}>
                                                                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                                                    {row.campaigns.slice(0, 2).map((c, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={c.CampaignName || "Single Coupon"}
                                                                            size="small"
                                                                            color="primary"
                                                                            variant="outlined"
                                                                        />
                                                                    ))}
                                                                    {row.campaigns.length > 2 && (
                                                                        <Chip
                                                                            label={`+${row.campaigns.length - 2} more`}
                                                                            size="small"
                                                                            color="default"
                                                                            variant="outlined"
                                                                        />
                                                                    )}
                                                                </Stack>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 120, padding: "8px 16px" }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {formatAdminDate(row.userCreatedAt)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 120, padding: "8px 16px" }}>
                                                                <Typography variant="body2" color="error.main" fontWeight="medium">
                                                                    {formatAdminDate(row.deletedDate)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 100, padding: "8px 16px" }}>
                                                                <Badge badgeContent={row.validCount} color="success">
                                                                    <CouponIcon color="action" />
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 100, padding: "8px 16px" }}>
                                                                <Badge badgeContent={row.usedCount} color="warning">
                                                                    <DeleteIcon color="action" />
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 100, padding: "8px 16px" }}>
                                                                <Badge badgeContent={row.earnedPoints} color="success">
                                                                    <PeopleIcon color="action" />
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 100, padding: "8px 16px" }}>
                                                                <Badge badgeContent={row.usedPoints} color="error">
                                                                    <RestoreIcon color="action" />
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 150, padding: "8px 16px" }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {row.reason}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ width: 120, padding: "8px 16px" }}>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {formatAdminDate(row.maxExpiryDate)}
                                                                </Typography>
                                                            </TableCell>
                                                    </TableRow>
                                                ))}
                                                
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                        
                                    {/* Pagination */}
                                    <Box sx={{ p: { xs: 1, sm: 1.5 }, borderTop: 1, borderColor: "divider" }}>
                                        <Stack 
                                            direction={{ xs: 'column', sm: 'row' }} 
                                            justifyContent="space-between" 
                                            alignItems={{ xs: 'stretch', sm: 'center' }}
                                            spacing={{ xs: 2, sm: 2 }}
                                        >
                                            {/* Left side - Info and items per page */}
                                            <Stack 
                                                direction={{ xs: 'column', sm: 'row' }} 
                                                spacing={{ xs: 1, sm: 2 }}
                                                alignItems={{ xs: 'center', sm: 'center' }}
                                            >
                                                <Typography 
                                                    variant="body2" 
                                                    color="text.secondary"
                                                    sx={{ 
                                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                        textAlign: { xs: 'center', sm: 'left' }
                                                    }}
                                                >
                                                    {pagination.total > 0 ? (
                                                        `Showing ${pagination.from} to ${pagination.to} of ${pagination.total} entries`
                                                    ) : (
                                                        'No entries found'
                                                    )}
                                                </Typography>
                                                {pagination.total > 0 && (
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                            Items per page:
                                                        </Typography>
                                                        <FormControl size="small" sx={{ minWidth: 70 }}>
                                                            <Select
                                                                value={rowsPerPage}
                                                                onChange={(e) => {
                                                                    setRowsPerPage(Number(e.target.value));
                                                                    setPage(1); // Reset to first page when changing items per page
                                                                }}
                                                                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, height: '32px' }}
                                                            >
                                                                <MenuItem value={10}>10</MenuItem>
                                                                <MenuItem value={25}>25</MenuItem>
                                                                <MenuItem value={50}>50</MenuItem>
                                                                <MenuItem value={100}>100</MenuItem>
                                                            </Select>
                                                        </FormControl>
                                                    </Stack>
                                                )}
                                            </Stack>

                                            {/* Right side - Page selection */}
                                            {pagination.total > 0 && pagination.last_page > 1 && (
                                                <Stack 
                                                    direction="row" 
                                                    spacing={1}
                                                    alignItems="center"
                                                    justifyContent={{ xs: 'center', sm: 'flex-end' }}
                                                    sx={{ flexWrap: 'wrap', gap: 1 }}
                                                >
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        disabled={page === 1}
                                                        onClick={() => setPage(1)}
                                                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                    >
                                                        First
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        disabled={page === 1}
                                                        onClick={() => setPage(page - 1)}
                                                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                    >
                                                        Prev
                                                    </Button>
                                                    
                                                    {/* Page number buttons */}
                                                    <Pagination
                                                        count={pagination.last_page}
                                                        page={page}
                                                        onChange={(event, value) => setPage(value)}
                                                        color="primary"
                                                        size="small"
                                                        showFirstButton
                                                        showLastButton
                                                        siblingCount={1}
                                                        boundaryCount={1}
                                                        sx={{
                                                            '& .MuiPaginationItem-root': {
                                                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                                                minWidth: { xs: '28px', sm: '32px' },
                                                                height: { xs: '28px', sm: '32px' },
                                                            }
                                                        }}
                                                    />
                                                    
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        disabled={page >= pagination.last_page}
                                                        onClick={() => setPage(page + 1)}
                                                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                    >
                                                        Next
                                                    </Button>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        disabled={page >= pagination.last_page}
                                                        onClick={() => setPage(pagination.last_page)}
                                                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 'auto', px: 1 }}
                                                    >
                                                        Last
                                                    </Button>
                                                    
                                                    {/* Jump to page input */}
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, whiteSpace: 'nowrap' }}>
                                                            Go to:
                                                        </Typography>
                                                        <TextField
                                                            type="number"
                                                            size="small"
                                                            value={page}
                                                            onChange={(e) => {
                                                                const value = parseInt(e.target.value);
                                                                if (value >= 1 && value <= pagination.last_page) {
                                                                    setPage(value);
                                                                }
                                                            }}
                                                            inputProps={{
                                                                min: 1,
                                                                max: pagination.last_page,
                                                                style: { 
                                                                    textAlign: 'center',
                                                                    fontSize: '0.75rem',
                                                                    padding: '4px 8px',
                                                                    width: '50px'
                                                                }
                                                            }}
                                                            sx={{
                                                                width: '60px',
                                                                '& .MuiOutlinedInput-root': {
                                                                    height: '32px',
                                                                }
                                                            }}
                                                        />
                                                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                                            / {pagination.last_page}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Box>
                                </>
                            )}
                        </CardContent>
                    </StyledCard>
                 </Box>
            </Box>
    );
};

export default DeletedCoupon;
